import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e?.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    alert('Thank you for your message! We\'ll get back to you soon.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e?.target?.name]: e?.target?.value
    });
  };

  const contactInfo = [
    {
      icon: "Mail",
      title: "Email Us",
      content: "support@sharegood.com",
      link: "mailto:support@sharegood.com"
    },
    {
      icon: "Phone",
      title: "Call Us",
      content: "+1 (555) 123-4567",
      link: "tel:+15551234567"
    },
    {
      icon: "MapPin",
      title: "Visit Us",
      content: "123 Impact Street, San Francisco, CA 94102",
      link: "https://maps.google.com"
    }
  ];

  const faqs = [
    {
      question: "How do I start donating?",
      answer: "Simply create a free account, browse verified nonprofits, and make your first donation. It takes less than 5 minutes!"
    },
    {
      question: "Are donations tax-deductible?",
      answer: "Yes! All donations to verified 501(c)(3) nonprofits are tax-deductible. We provide automatic tax receipts for all donations."
    },
    {
      question: "How do you verify nonprofits?",
      answer: "We thoroughly vet each nonprofit, verifying their 501(c)(3) status, financial transparency, and impact reporting before adding them to our platform."
    },
    {
      question: "Can I set up recurring donations?",
      answer: "Absolutely! You can set up monthly or yearly recurring donations to any nonprofit on our platform."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-cream-50 to-white">
      <Header />

      {/* Hero Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Get in{' '}
              <span className="bg-gradient-to-r from-coral-500 to-orange-500 bg-clip-text text-transparent">
                Touch
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto">
              Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-orange-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Your Name
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData?.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData?.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Subject
                  </label>
                  <Input
                    type="text"
                    name="subject"
                    value={formData?.subject}
                    onChange={handleChange}
                    placeholder="How can we help?"
                    required
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData?.message}
                    onChange={handleChange}
                    rows={6}
                    placeholder="Tell us more about your inquiry..."
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500 resize-none text-gray-900"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-gradient-to-r from-coral-500 to-orange-500 hover:from-coral-600 hover:to-orange-600 text-white"
                  iconName="Send"
                  iconPosition="right"
                >
                  Send Message
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-orange-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <div className="space-y-6">
                  {contactInfo?.map((info, index) => (
                    <a
                      key={index}
                      href={info?.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start space-x-4 p-4 rounded-lg hover:bg-orange-50 transition-colors group"
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-coral-500 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <Icon name={info?.icon} size={24} className="text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">{info?.title}</h3>
                        <p className="text-gray-700">{info?.content}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 border border-teal-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Office Hours</h3>
                <div className="space-y-2 text-gray-700">
                  <p className="flex justify-between">
                    <span className="font-medium">Monday - Friday:</span>
                    <span>9:00 AM - 6:00 PM PST</span>
                  </p>
                  <p className="flex justify-between">
                    <span className="font-medium">Saturday:</span>
                    <span>10:00 AM - 4:00 PM PST</span>
                  </p>
                  <p className="flex justify-between">
                    <span className="font-medium">Sunday:</span>
                    <span>Closed</span>
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-8 border border-orange-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Follow Us</h3>
                <div className="flex space-x-4">
                  <button className="w-12 h-12 rounded-full bg-gradient-to-br from-coral-500 to-orange-500 hover:from-coral-600 hover:to-orange-600 flex items-center justify-center text-white transition-all transform hover:scale-110">
                    <Icon name="Twitter" size={20} />
                  </button>
                  <button className="w-12 h-12 rounded-full bg-gradient-to-br from-coral-500 to-orange-500 hover:from-coral-600 hover:to-orange-600 flex items-center justify-center text-white transition-all transform hover:scale-110">
                    <Icon name="Facebook" size={20} />
                  </button>
                  <button className="w-12 h-12 rounded-full bg-gradient-to-br from-coral-500 to-orange-500 hover:from-coral-600 hover:to-orange-600 flex items-center justify-center text-white transition-all transform hover:scale-110">
                    <Icon name="Instagram" size={20} />
                  </button>
                  <button className="w-12 h-12 rounded-full bg-gradient-to-br from-coral-500 to-orange-500 hover:from-coral-600 hover:to-orange-600 flex items-center justify-center text-white transition-all transform hover:scale-110">
                    <Icon name="Linkedin" size={20} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-700">
              Quick answers to common questions
            </p>
          </div>

          <div className="space-y-6">
            {faqs?.map((faq, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-orange-50 to-transparent rounded-2xl p-8 border border-orange-100"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <Icon name="HelpCircle" size={24} className="text-coral-500 mr-3" />
                  {faq?.question}
                </h3>
                <p className="text-gray-700 leading-relaxed ml-9">{faq?.answer}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-700 mb-4">Still have questions?</p>
            <Button
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-coral-500 to-orange-500 hover:from-coral-600 hover:to-orange-600 text-white"
            >
              Contact Support
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;